import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const QUICK_TAGS: Record<'mentor' | 'course', string[]> = {
  mentor: ['AI 研究', 'ML 工程', '產品管理', '系統設計', '模擬面試'],
  course: ['大型語言模型', 'ML 工程', '產品管理', '資料科學', '面試衝刺'],
};

const HeroSection = () => {
  const [searchType, setSearchType] = useState<'mentor' | 'course'>('mentor');
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (q: string = query) => {
    const trimmed = q.trim();
    if (!trimmed) {
      navigate(searchType === 'mentor' ? '/mentors' : '/courses');
      return;
    }
    navigate(`${searchType === 'mentor' ? '/mentors' : '/courses'}?q=${encodeURIComponent(trimmed)}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleTagClick = (tag: string) => {
    setQuery(tag);
    handleSearch(tag);
  };

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden bg-navy-900"
      style={{ paddingTop: '64px' }}
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="/assets/readdy/heroBGnexus2026-1920x1080-landscape-5bae55d8.jpg"
          alt="AI Neural Network Background"
          className="w-full h-full object-cover object-top opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/85 to-navy-900/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-transparent to-transparent" />
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 flex items-center gap-16">
        <div className="flex-1 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-600/20 border border-violet-500/30 rounded-full mb-5 sm:mb-7">
            <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-pulse" />
            <span className="text-violet-300 text-[10px] sm:text-xs font-medium tracking-widest uppercase">
              NexusGo — 通往全球 AI 科技職涯的橋梁
            </span>
          </div>

          <h1 className="font-bold text-white leading-tight mb-4 sm:mb-6 tracking-tight">
            <span className="block text-base sm:text-lg font-medium text-slate-300 mb-1">
              連結來自頂尖跨國公司的
            </span>
            <span className="block text-5xl sm:text-6xl xl:text-7xl text-violet-400 tracking-tight">
              精英 1%
            </span>
            <span className="block text-3xl sm:text-4xl xl:text-5xl mt-1">
              全球 AI 導師
            </span>
          </h1>

          <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-6 sm:mb-8 max-w-xl">
            透過來自{' '}
            <span className="text-white font-semibold">全球頂尖外商公司</span>{' '}
            內部人士的個人化 1 對 1 職涯諮詢，掌握 AI 時代的職場競爭優勢
          </p>

          {/* 搜索框 */}
          <div className="mb-7 sm:mb-8">
            <div className="inline-flex items-center bg-white/10 rounded-lg p-1 mb-3 border border-white/10">
              {(['mentor', 'course'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => { setSearchType(t); setQuery(''); }}
                  className={`px-4 py-1.5 rounded-md text-xs font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 ${
                    searchType === t ? 'bg-violet-600 text-white' : 'text-white/60 hover:text-white'
                  }`}
                >
                  {t === 'mentor' ? <><i className="ri-user-star-line mr-1.5" />找導師</> : <><i className="ri-book-open-line mr-1.5" />找課程</>}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <div className="relative flex-1">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                  <i className="ri-search-line text-slate-400 text-base" />
                </div>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={searchType === 'mentor' ? '搜尋導師姓名、公司、技能...' : '搜尋課程名稱、講師、主題...'}
                  className="w-full pl-11 pr-4 py-3.5 bg-white/10 text-white placeholder-slate-400 rounded-xl border border-white/15 focus:outline-none focus:border-violet-400 focus:bg-white/15 text-sm transition-all"
                />
              </div>
              <button
                onClick={() => handleSearch()}
                className="px-5 py-3.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center gap-2"
              >
                搜尋
                <i className="ri-arrow-right-line" />
              </button>
            </div>

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className="text-slate-500 text-xs whitespace-nowrap">熱門：</span>
              {QUICK_TAGS[searchType].map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className="px-2.5 py-1 bg-white/8 hover:bg-white/15 text-white/60 hover:text-white text-xs rounded-full cursor-pointer transition-all border border-white/10 whitespace-nowrap"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* CTA 按钮 */}
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <Link
              to="/courses"
              className="px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-all text-center flex items-center justify-center gap-1.5"
            >
              <i className="ri-book-open-line" />
              瀏覽全部課程
            </Link>
            <Link
              to="/mentors"
              className="px-5 py-3 bg-white/15 hover:bg-white/25 text-white text-sm font-semibold rounded-lg border border-white/30 cursor-pointer whitespace-nowrap transition-all text-center flex items-center justify-center gap-1.5"
            >
              <i className="ri-user-star-line" />
              瀏覽全部導師
            </Link>
          </div>

          <div className="flex items-center gap-4 sm:gap-6 mt-8 sm:mt-10 pt-8 sm:pt-10 border-t border-white/10">
            <div className="flex -space-x-2">
              {[
                '/assets/readdy/facea01-40x40-squarish-4da3c2af.jpg',
                '/assets/readdy/faceb02-40x40-squarish-d63f0d80.jpg',
                '/assets/readdy/facec03-40x40-squarish-497d528c.jpg',
              ].map((src, i) => (
                <img key={i} src={src} alt="Student" className="w-9 h-9 rounded-full border-2 border-navy-900 object-cover object-top" />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1 mb-0.5">
                {[1, 2, 3, 4, 5].map((s) => <i key={s} className="ri-star-fill text-amber-400 text-xs" />)}
                <span className="text-white font-semibold text-sm ml-1">4.97</span>
              </div>
              <p className="text-slate-400 text-xs">來自 1,200+ 學員的真實評價</p>
            </div>
          </div>
        </div>

        <div className="hidden lg:block flex-shrink-0">
          <div className="relative w-[400px] h-[520px]">
            <div className="absolute -top-3 -left-3 w-full h-full border border-violet-500/25 rounded-2xl" />
            <div className="absolute -bottom-3 -right-3 w-full h-full border border-violet-500/12 rounded-2xl" />
            <img
              src="/assets/readdy/teamHero2026-400x520-portrait-ce68967c.jpg"
              alt="NexusGo Elite Mentor Team"
              className="w-full h-full object-cover object-top rounded-2xl"
            />
            <div className="absolute -left-14 bottom-20 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3.5 w-44">
              <div className="text-white font-bold text-2xl">500+</div>
              <div className="text-slate-300 text-xs mt-0.5">精選全球頂尖導師</div>
            </div>
            <div className="absolute -right-14 top-20 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3.5 w-44">
              <div className="text-violet-300 font-bold text-2xl">95%</div>
              <div className="text-slate-300 text-xs mt-0.5">成功進入目標外商</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
